import {useState, useEffect} from 'react';
import {View, Text, StyleSheet, TouchableOpacity} from 'react-native';
import Alert from "@/lib/Alert";
import {VideoView, useVideoPlayer} from 'expo-video';
import {AppDataMediaItem} from "@/data/app-data-interfaces";
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {ThemeType} from "@/theming/baseTheme";
import {getVideoUrl} from "@/data/app-data-api";
import {PlayButton} from "@/components/common/PlayButton";
import {LoadSpinner} from "@/components/common/LoadSpinner";
import {formatDuration} from "@/lib/utils";
import {useAddLastViewed} from "@/data/user-data-hooks";
import {Image} from "expo-image";

interface VideoPlayerProps {
    video: AppDataMediaItem;
    orgId: string
}

export const VideoPlayer = ({
                                                            orgId,
                                                            video
                                                        }: VideoPlayerProps) => {
    const {theme} = useCurrentTheme();
    const styles = createStyles(theme);
    const [isLoading, setIsLoading] = useState(false);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const addLastViewed = useAddLastViewed();

    const player = useVideoPlayer(videoUrl ? {uri: videoUrl, useCaching: true} : null, (player) => {
        player.loop = true;
        player.play();
    });

    // Use effect to replace the video source when URL changes
    useEffect(() => {
        if (videoUrl && player) {
            player.replace({uri: videoUrl});
        }
    }, [videoUrl, player]);

    const handlePlayPress = async () => {
        addLastViewed.mutate({orgId, videoId: video.id})
        if (videoUrl) {
            // Video URL already loaded, just play
            player.play();
            return;
        }

        setIsLoading(true);

        try {
            const url = await getVideoUrl(video.id);
            console.log('loaded url', url);
            setVideoUrl(url);
            // The player will automatically start playing due to the useVideoPlayer configuration
        } catch (error) {
            console.error('Error loading video:', error);
            Alert.alert(
                'Error',
                'Failed to load video. Please try again.',
                [{text: 'OK'}]
            );
        } finally {
            setIsLoading(false);
        }
    };

    // Show video player if we have a URL
    if (videoUrl && player) {
        return (
            <View style={styles.videoPlayerContainer}>
                <VideoView
                    player={player}
                    style={styles.videoPlayer}
                    allowsFullscreen={true}
                    allowsPictureInPicture={true}
                    startsPictureInPictureAutomatically={true}
                />
            </View>
        );
    }

    // Show thumbnail with play button
    return (
        <View style={styles.videoPlayerContainer}>
            <TouchableOpacity
                style={styles.videoThumbnail}
                onPress={handlePlayPress}
                disabled={isLoading}
            >
                <Image
                    source={video.image.lg}
                    style={styles.videoImage}
                    placeholder={{blurhash: video.imageBlurhash}}
                    contentFit="cover"
                    transition={200}
                />

                <View style={styles.playButton}>
                    {isLoading ? (
                        <LoadSpinner size={80}/>
                    ) : (
                        <PlayButton size={80} onPress={handlePlayPress}/>
                    )}
                </View>

                {video.duration > 0 && <View style={styles.videoDuration}>
                    <Text style={styles.videoDurationText}>{formatDuration(video.duration)}</Text>
                </View>}

                <View style={[styles.videoProgress]}/>
            </TouchableOpacity>
        </View>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    videoPlayerContainer: {
        width: '100%',
        height: 250,
        backgroundColor: '#000',
        position: 'relative',
    },
    videoThumbnail: {
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
    },
    videoImage: {
        width: '100%',
        height: '100%',
        position: 'absolute',
    },
    videoPlayer: {
        width: '100%',
        height: '100%',
    },
    playButton: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    videoDuration: {
        position: 'absolute',
        bottom: 12,
        right: 12,
        backgroundColor: theme.colors.background,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 4,
        zIndex: 1,
    },
    videoDurationText: {
        color: theme.colors.text,
        fontSize: 12,
        fontWeight: '600',
    },
    videoProgress: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        height: 3,
        backgroundColor: theme.colors.primary,
        zIndex: 1,
        width: '100%'
    },
});
